import { type ObjectMode } from '@databases/types';
import { type Database, DatabaseFieldType } from '@databases/types/database';
import {
  DatabaseCardWidth,
  DatabaseFilterType,
  DatabaseLayoutType,
} from '@databases/types/database/view';
import { createField, getExistingDatabase } from '@databases/utils/database/utils';

import { generateValuesForEntriesById } from '../utils';

import { databasesToContentId } from './constants';

export const getDatabaseWithFilters = (): Database<ObjectMode.EXISTING> => {
  const baseDatabase = getExistingDatabase({
    contentId: databasesToContentId['databaseWithFilters'],
    databaseName: 'Database with filters',
  });

  const fields = [
    ...Array.from({ length: 3 }, (_, index) => ({
      ...createField(DatabaseFieldType.TEXT),
      name: `Text Field ${index}`,
    })),
  ];

  const entriesById = generateValuesForEntriesById(baseDatabase.entries, fields);

  return {
    ...baseDatabase,
    fields: fields.map((field) => field.id),
    fieldsById: Object.fromEntries(fields.map((field) => [field.id, field])),
    entriesById,
    entries: Object.keys(entriesById),
    views: [
      {
        id: '0',
        name: 'All entries',
        settings: {
          layout: {
            coverFieldId: 'default',
            type: DatabaseLayoutType.TABLE,
            cardWidth: DatabaseCardWidth.MEDIUM,
            showFieldNames: true,
            showNumberedEntries: false,
          },
          calculations: {},
          sorts: [],
        },
      },
      {
        id: '1',
        name: 'View with filter',
        settings: {
          layout: {
            coverFieldId: 'default',
            type: DatabaseLayoutType.TABLE,
            cardWidth: DatabaseCardWidth.MEDIUM,
            showFieldNames: true,
            showNumberedEntries: false,
          },
          filter: {
            id: 'filter-1',
            type: DatabaseFilterType.AND,
            value: [
              {
                id: 'filter-2',
                type: DatabaseFilterType.CONTAINS,
                fieldId: fields[0].id,
                not: true,
                value: 'text 3',
              },
            ],
          },
          calculations: {},
          sorts: [],
        },
      },
      {
        id: '2',
        name: 'View with sort',
        settings: {
          layout: {
            coverFieldId: 'default',
            type: DatabaseLayoutType.TABLE,
            cardWidth: DatabaseCardWidth.MEDIUM,
            showFieldNames: true,
            showNumberedEntries: false,
          },
          sort: {
            ascending: false,
            fieldId: fields[0].id,
          },
          calculations: {},
          sorts: [
            {
              ascending: false,
              fieldId: fields[0].id,
            },
          ],
        },
      },
      {
        id: '3',
        name: 'View with hidden fields',
        settings: {
          fields: {
            [fields[0].id]: true,
            [fields[1].id]: false,
            [fields[2].id]: true,
          },
          layout: {
            coverFieldId: 'default',
            type: DatabaseLayoutType.TABLE,
            cardWidth: DatabaseCardWidth.MEDIUM,
            showFieldNames: true,
            showNumberedEntries: false,
          },
          calculations: {},
          sorts: [],
        },
      },
      {
        id: '4',
        name: 'View with equals filter',
        settings: {
          layout: {
            coverFieldId: 'default',
            type: DatabaseLayoutType.TABLE,
            cardWidth: DatabaseCardWidth.MEDIUM,
            showFieldNames: true,
            showNumberedEntries: false,
          },
          filter: {
            id: 'filter-3',
            type: DatabaseFilterType.AND,
            value: [
              {
                id: 'filter-4',
                type: DatabaseFilterType.EQUALS,
                fieldId: fields[0].id,
                value: {
                  type: DatabaseFieldType.TEXT,
                  value: { plain: 'text 3' },
                },
              },
            ],
          },
          calculations: {},
          sorts: [],
        },
      },
    ],
  };
};
